import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Heart, Award } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [treatments, setTreatments] = useState([]);

  useEffect(() => {
    const fetchTreatments = async () => {
      try {
        const response = await axios.get(`${API}/treatments`);
        setTreatments(response.data.treatments.slice(0, 4));
      } catch {
        // silently handle fetch failure
      }
    };
    fetchTreatments();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="bg-clinic-bg" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Photo Background - faded to the right */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets-wrfwihn1.emergentagent.net/job_0b41d704-a68a-4dc5-aa3c-437daaaec2be/artifacts/b1b0896df5aa3375_DSC02048-31.webp"
            alt=""
            className="w-full h-full object-cover object-center"
            data-testid="hero-image"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to left, #FEFEF6 30%, rgba(254,254,246,0.6) 55%, rgba(254,254,246,0.15) 80%, transparent 100%)' }} />
        </div>

        {/* Content */}
        <div className="container-clinic w-full relative z-10 pt-24 md:pt-0 flex justify-end">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-2xl text-right"
          >
            <motion.p
              variants={fadeInUp}
              className="text-caption mb-6"
            >
              Consultant Plastic Surgeon
            </motion.p>
            <motion.h1
              variants={fadeInUp}
              className="text-heading-1 text-clinic-charcoal mb-8"
            >
              Refined surgical<br />expertise, with care
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-body-lg max-w-lg mb-10"
            >
              Mr Kavish Maheshwari provides consultant-led plastic, reconstructive, 
              and aesthetic surgery. An ethical, evidence-based approach focused 
              on safety, natural results, and your wellbeing.
            </motion.p>
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-end"
            >
              <Link
                to="/consultation"
                className="btn-primary"
                data-testid="hero-consultation-btn"
              >
                Book a Consultation
              </Link>
              <Link
                to="/treatments"
                className="btn-secondary"
                data-testid="hero-treatments-btn"
              >
                View Treatments
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white" data-testid="values-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-caption mb-4">Our Approach</p>
            <h2 className="text-heading-2 text-clinic-charcoal">
              Precision with purpose
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                icon: Shield,
                title: 'Safety First',
                description: 'NHS-trained expertise with rigorous adherence to the highest surgical standards and patient safety protocols.'
              },
              {
                icon: Heart,
                title: 'Patient-Centred',
                description: 'Every consultation begins with understanding your goals. No pressure, no upselling—just honest, informed guidance.'
              },
              {
                icon: Award,
                title: 'Evidence-Based',
                description: 'Treatments grounded in proven medical evidence and refined surgical techniques, not trends or marketing.'
              }
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-8"
                data-testid={`value-card-${index}`}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 mb-6 border border-clinic-border">
                  <value.icon size={24} className="text-clinic-charcoal" />
                </div>
                <h3 className="text-heading-3 text-clinic-charcoal mb-4">{value.title}</h3>
                <p className="text-body-lg">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments Preview */}
      <section className="section-padding" data-testid="treatments-preview-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12"
          >
            <div>
              <p className="text-caption mb-4">Expertise</p>
              <h2 className="text-heading-2 text-clinic-charcoal">
                Specialist treatments
              </h2>
            </div>
            <Link
              to="/treatments"
              className="mt-6 md:mt-0 inline-flex items-center gap-2 text-sm font-body font-medium text-clinic-charcoal hover:text-clinic-stone transition-colors"
              data-testid="view-all-treatments-link"
            >
              View all treatments
              <ArrowRight size={16} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {treatments.map((treatment, index) => (
              <motion.div
                key={treatment.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/treatments/${treatment.id}`}
                  className="card-service group block"
                  data-testid={`treatment-card-${treatment.id}`}
                >
                  <div>
                    <p className="text-caption mb-3">{treatment.subtitle}</p>
                    <h3 className="text-heading-3 text-clinic-charcoal mb-4 group-hover:text-clinic-stone transition-colors">
                      {treatment.title}
                    </h3>
                    <p className="text-body-lg line-clamp-2">{treatment.description}</p>
                  </div>
                  <div className="mt-6 pt-6 border-t border-clinic-border flex items-center justify-between">
                    <span className="text-sm font-body text-clinic-stone">
                      {treatment.procedures.length} procedures
                    </span>
                    <ArrowRight size={18} className="text-clinic-charcoal group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding bg-clinic-muted" data-testid="about-preview-section">
        <div className="container-clinic">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="aspect-[3/4] relative overflow-hidden">
                <img
                  src="https://customer-assets-wrfwihn1.emergentagent.net/job_0b41d704-a68a-4dc5-aa3c-437daaaec2be/artifacts/7c67b6df33033e3f_DSC02424-144.webp"
                  alt="Mr Kavish Maheshwari - Consultant Plastic Surgeon"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 15%' }}
                  data-testid="about-preview-image"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-caption mb-4">Your Surgeon</p>
              <h2 className="text-heading-2 text-clinic-charcoal mb-6">
                Mr Kavish Maheshwari
              </h2>
              <p className="text-body-lg mb-6">
                MBBS, MS (Gen Surg), MRCS, MCh (Plast), FRCS (Plast)
              </p>
              <p className="text-body-lg mb-8">
                A consultant plastic surgeon with comprehensive NHS training and 
                expertise across reconstructive and aesthetic surgery. Committed to 
                providing thoughtful, individualised care with an emphasis on natural 
                results and patient safety.
              </p>
              <Link
                to="/about"
                className="btn-secondary"
                data-testid="learn-more-about-btn"
              >
                Learn More
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="cta-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-heading-2 text-clinic-charcoal mb-6">
              Begin with a conversation
            </h2>
            <p className="text-body-lg mb-10">
              Every journey starts with understanding. Book a consultation to discuss 
              your goals in a confidential, no-pressure environment.
            </p>
            <Link
              to="/contact"
              className="btn-primary"
              data-testid="cta-consultation-btn"
            >
              Request a Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
